// File: RoomCheckOut.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "utils/notifications";
import { validateAdvanceRow } from "../HELPERS/Helpers"



import {
  getAllChecInRoom,
  getAllPaymentMethods,
  Search,
  Update,
} from "adminApi/RoomCheCkOutApi/RoomCheCkOut";

import { AdvanceDetailsRules } from "validation/ValidationRules";
import { validateForm, validateField } from "utils/validation";
import Spinner from "Common/Globle/Spinner";
import RoomCheckOutForm from "./Componates/RoomCheckOutForm";
import RoomCheckOutTable from "./Componates/RoomCheckOutTable";

import fields from "./json/RoomCheckOutFields.json";
import useFormChange from "Hooks/useFormChange";
import useFetchAllData from "Hooks/useFetchAllData";
import useFormSubmit from "Hooks/useFormSubmit";

// Rounds to 2 decimals before comparing, avoids floating point mismatches
// like 499.9999999999999 !== 500.
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const RoomCheckOut = () => {
  const { formdata, Advance_Details } = fields;
  const location = useLocation();
  const navigate = useNavigate();


  /* -------------------- STATE -------------------- */
  const [searchData, setSearchData] = useState(null);
  const [RoomsOpction, setRoomsOpction] = useState([])

  const emptyPaymentDetail = {
    ...Object.fromEntries(Advance_Details.map((f) => [f.name, ""])),
    Unic_key: crypto.randomUUID(),
  };

  /* -------------------- FORM -------------------- */
  const initialValues = {
    ...formdata.reduce(
      (acc, field) => ({ ...acc, [field.name]: "" }),
      {}
    ),
    PaymentDetail: [emptyPaymentDetail],
  };

  const { values, setValues, errors, handleChange, setErrors } = useFormChange(
    initialValues,
    AdvanceDetailsRules
  );

  /* -------------------- MASTER DATA -------------------- */
  const {
    data: CheckInRoomsOpction = [],
    loading: CheckInRoomsOpctionLoading,
    refetch: RCheckInRoomOption
  } = useFetchAllData(getAllChecInRoom);

  const {
    data: paymentMethodTypesOption = [],
    loading: paymentMethodTypesOptionLoading
  } = useFetchAllData(getAllPaymentMethods);

  /* -------------------- ROOM OPTIONS -------------------- */
  useEffect(() => {
    setRoomsOpction(CheckInRoomsOpction);
  }, [CheckInRoomsOpction, RCheckInRoomOption]);

  /* -------------------- SEARCH -------------------- */
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = useCallback(async (bookingId) => {
    const id = bookingId || values.booking_id;
    if (!id) return;

    setSearchLoading(true);

    try {
      const res = await Search(id);
      if (res.data.success) {
        setSearchData(res?.data?.data ?? null);
      } else {
        showError(res?.data?.message || res?.data?.error || "Failed to fetch booking.");
        setSearchData(null);
      }
    } catch (err) {
      console.error("Search Error:", err);
      showError(err?.data?.message || err?.data?.error || "Failed to fetch booking.");
      setSearchData(null);
    } finally {
      setSearchLoading(false);
    }
  }, [values.booking_id]);

  useEffect(() => {
    const stateBookingId = location.state?.booking_id;
    if (stateBookingId) {
      handleSearch(stateBookingId);
    }
  }, [location.state?.booking_id, handleSearch]);

  /* -------------------- DUE AMOUNT (from backend, no client calc) -------------------- */
  // billing_detail.balance_due follows the "signed" convention used by the
  // Billing Summary table: NEGATIVE means the guest still owes that much,
  // POSITIVE means they've overpaid and have a credit balance.
  //
  // AdvanceDetailsList works with a plain "amount still owed" number, so
  // we normalize here: if balance_due is negative, the owed amount is its
  // absolute value; if it's zero or positive (paid in full / in credit),
  // nothing more is owed.
  const dueAmount = round2(searchData?.billing_detail?.amount_owed ?? 0);
  const creditAmount = round2(searchData?.billing_detail?.credit_balance ?? 0);

  const validatePaymentDetails = (vals) => {
    const due = Number(dueAmount || 0);

    // No payment required
    if (due <= 0) {
      return {};
    }

    const rows = vals.PaymentDetail || [];

    // At least one payment row is required
    if (!rows.length) {
      return {
        PaymentDetail: [
          {
            payment_method: "Payment details are required.",
            advance_amount: "Advance Amount is required.",
          },
        ],
      };
    }

    const paymentErrors = rows.map((row) => {
      const rowErr = {};

      // Existing field validation
      Object.keys(AdvanceDetailsRules).forEach((fieldName) => {
        const msg = validateField(
          fieldName,
          row[fieldName],
          AdvanceDetailsRules
        );

        if (msg) {
          rowErr[fieldName] = msg;
        }
      });

      // Custom validation (Cash doesn't require Transaction ID)
      Object.assign(
        rowErr,
        validateAdvanceRow(row, paymentMethodTypesOption)
      );

      return rowErr;
    });

    const hasErrors = paymentErrors.some(
      (rowErr) => Object.keys(rowErr).length > 0
    );

    return hasErrors
      ? { PaymentDetail: paymentErrors }
      : {};
  };

  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      const formData = new FormData();
      Object.entries(formValues).forEach(([key, val]) => {
        if (key === "PaymentDetail") {
          formData.append(key, JSON.stringify(val));
        } else {
          formData.append(key, val ?? "");
        }
      });

      return Update({ id: values.booking_id, data: formData });
    },
    (vals) => {
      // An advance can be less than, equal to, or more than dueAmount.
      // Paying less is a normal partial advance; paying more becomes a
      // credit balance (handled/displayed by AdvanceDetailsList). Neither
      // case blocks submission - only the standard field-level rules
      // from AdvanceDetailsRules (required fields, formats, etc.) do.
      const errs = validatePaymentDetails(vals);

      setErrors((prev) => ({
        ...prev,
        ...errs,
      }));

      return errs;
    },
    () => { navigate(-1); },
    "Room Check Out successfully"
  );

  const isLoading = CheckInRoomsOpctionLoading || paymentMethodTypesOptionLoading || loadingSubmit || searchLoading

  return (
    <>
      {isLoading && <Spinner isSpinner />}

      <RoomCheckOutForm
        fields={formdata}
        values={values}
        errors={errors}
        handleChange={handleChange}
        CheckInRoomsOpction={RoomsOpction}
        handleSearch={() => handleSearch()}
        loading={CheckInRoomsOpctionLoading}
      />

      {searchData && (
        <RoomCheckOutTable
          RoomCkeckOutDetail={searchData}
          paymentMethodTypesOption={paymentMethodTypesOption}
          handleSubmit={(e) => handleSubmit(e, values)}
          loading={loadingSubmit}
          values={values}
          setValues={setValues}
          setErrors={setErrors}
          errors={errors}
          Advance_Details={Advance_Details}
          emptyPaymentDetail={emptyPaymentDetail}
          dueAmount={dueAmount}
          creditAmount={creditAmount}
          id={values.booking_id}
          AdvanceDetailsRules={AdvanceDetailsRules}
        />
      )}
    </>
  );
};

export default RoomCheckOut;